import { createContext, useContext, useState, useEffect } from "react";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { resetPaymentState } from "@/utils/PaymentHelper";
import { useSessionManager } from "@/hooks/useSessionManager";
import { getDeviceInfo, getDeviceFingerprint } from "@/utils/deviceInfo";
import { logSecurityEvent } from "@/utils/securityUtils";
import { savePasswordHistory } from "@/utils/passwordSecurity";

type AuthContextType = {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  loading: true,
  signOut: async () => {}, 
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Get user ID and session ID for session management
  const userId = session?.user?.id;
  const sessionId = session?.access_token;
  
  // Initialize session manager
  const { 
    recordSession,
    checkSessionValidity,
    updateSessionActivity,
    handleInvalidSession,
    isSessionValid
  } = useSessionManager(userId || null, sessionId || null);

  // Poll for session validity (every 30 seconds)
  useEffect(() => {
    if (!userId || !sessionId) return;
    
    // Record the session when it's first established
    const recordNewSession = async () => {
      try {
        await recordSession();
      } catch (error) {
        console.error("Failed to record session:", error);
      }
    };
    
    recordNewSession();
    
    // Set up polling interval to check if session is still valid
    const intervalId = setInterval(async () => {
      try {
        const isValid = await checkSessionValidity();
        
        if (!isValid) {
          // Session is invalid, clean up and redirect
          handleInvalidSession();
          clearInterval(intervalId);
        } else {
          // Session is still valid, update last activity
          updateSessionActivity();
        }
      } catch (error) {
        console.error("Error checking session validity:", error);
      }
    }, 30000); // Check every 30 seconds
    
    return () => {
      clearInterval(intervalId);
    };
  }, [userId, sessionId, recordSession, checkSessionValidity, updateSessionActivity, handleInvalidSession]);

  // Handle invalid session
  useEffect(() => {
    if (!isSessionValid && session) {
      try {
        handleInvalidSession();
      } catch (error) {
        console.error("Error handling invalid session:", error);
      }
    }
  }, [isSessionValid, session, handleInvalidSession]);

  useEffect(() => {
    console.log("AuthProvider initializing...");
    
    // First set up the auth listener to avoid race conditions
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.log("Auth state changed:", event, newSession?.user?.email);
      
      if (event === 'PASSWORD_RECOVERY') {
        console.log("Password recovery event detected");
        // Just update the session, the reset password page will handle the UI
        setSession(newSession);
      } 
      else if (event === 'TOKEN_REFRESHED') {
        console.log("Token refreshed event detected");
        setSession(newSession);
      }
      else if (event === 'SIGNED_OUT') {
        // Clean up any local session data
        console.log("Signed out event detected");
        setSession(null);
        
        // Reset payment state on sign out - GUARANTEED cleanup
        resetPaymentState();
        
        // Comprehensive cleanup for more reliable logout
        if (typeof window !== 'undefined') {
          // Clear all auth-related localStorage items
          localStorage.removeItem('edaptica-auth-token');
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('code_verifier');
          localStorage.removeItem('sb-fvoskhrltpygiduhzcgq-auth-token');
          localStorage.removeItem('lastSuccessfulPayment');
          localStorage.removeItem('subscription_recovery_attempted');
          
          // Clear any session storage items related to password reset
          sessionStorage.removeItem('password_reset_code_verifier');
          
          // Clear cookies that might be related to auth
          document.cookie.split(';').forEach(cookie => {
            const [name] = cookie.split('=');
            if (name.trim().includes('supabase') || name.trim().includes('sb-') || name.trim().includes('edaptica')) {
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
            }
          });
        }
      } else {
        console.log("Setting session from auth state change event:", event);
        setSession(newSession);
      }
      
      setLoading(false);
    });

    // Then check the current session
    const checkCurrentSession = async () => {
      try {
        console.log("Checking current session...");
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          toast.error("Authentication error. Please try again.");
          setSession(null);
        } else {
          console.log("Initial session check:", data?.session?.user?.email || "No session");
          setSession(data?.session);
          
          // If we have a valid session, record it
          if (data?.session?.user?.id) {
            try {
              await recordSession();
            } catch (err) {
              console.error("Failed to record initial session:", err);
            }
          }
        }
      } catch (err) {
        console.error("Exception during getSession:", err);
        setSession(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkCurrentSession();

    return () => {
      console.log("Cleaning up auth subscription");
      subscription.unsubscribe();
    };
  }, [recordSession]);

  // Enhanced signOut method with better security
  const signOut = async () => {
    try {
      console.log("Signing out...");
      
      // Record security event before sign out
      if (userId) {
        try {
          await logSecurityEvent(
            userId,
            'user_logout',
            'User initiated logout',
            { 
              deviceInfo: getDeviceInfo(),
              fingerprint: getDeviceFingerprint() 
            }
          );
        } catch (logError) {
          console.error("Error logging security event:", logError);
        }
      }
      
      // Notify UI first that logout is in progress
      toast.info("Logging out...");
      
      // Reset payment state to clear recovery flags and payment data - DO EARLY
      resetPaymentState();
      
      // First clean up the local storage/cookies to prevent session conflicts
      if (typeof window !== 'undefined') {
        localStorage.removeItem('edaptica-auth-token');
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('code_verifier');
        localStorage.removeItem('sb-fvoskhrltpygiduhzcgq-auth-token');
        localStorage.removeItem('lastSuccessfulPayment');
        localStorage.removeItem('subscription_recovery_attempted');
        
        // Clear session storage items
        sessionStorage.removeItem('password_reset_code_verifier');
        
        // Clear potentially problematic cookies
        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.split('=');
          if (name.trim().includes('supabase') || name.trim().includes('sb-') || name.trim().includes('edaptica')) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
          }
        });
      }
      
      // Set session to null first to update UI immediately
      setSession(null);
      
      // Delete from active_sessions table if we have user and session IDs
      if (userId && sessionId) {
        try {
          await supabase
            .from('active_sessions')
            .delete()
            .eq('user_id', userId)
            .eq('session_id', sessionId);
        } catch (error) {
          console.error("Error deleting session from database:", error);
        }
      }
      
      // Now call the supabase signOut method with specific options
      const { error } = await supabase.auth.signOut({
        scope: 'global' // Make sure to log out from all tabs/windows
      });
      
      if (error) {
        console.error("Error from Supabase during signOut:", error);
        // Even if there's an error, we still want to clear local state
        toast.error("There was an issue signing out from the server, but you've been logged out locally.");
      } else {
        toast.success("Successfully logged out");
      }
      
      // Force a redirect to the auth page after a brief delay
      setTimeout(() => {
        window.location.href = '/auth';
      }, 500);
    } catch (error) {
      console.error("Exception during sign out:", error);
      toast.error("There was an issue signing out. Please try again or refresh the page.");
      
      // As a fallback, force the page to reload which often clears auth state issues
      setTimeout(() => {
        window.location.href = '/auth';
      }, 1000);
    }
  };

  // When a session is established, record password history if it's new
  useEffect(() => {
    const recordPasswordHistory = async () => {
      if (session?.user?.id) {
        try {
          // Get the latest password history entry for this user
          const { data: latestHistory } = await supabase
            .from('password_history')
            .select('created_at')
            .eq('user_id', session.user.id)
            .order('created_at', { ascending: false })
            .limit(1);
            
          // If this is a new session with no recent history, record the password hash
          // This is mainly for existing users when we first deploy this feature
          if (!latestHistory || !latestHistory.length) {
            // We don't have the actual hash, but we'll record a placeholder
            // Future password changes will record the actual hash
            await savePasswordHistory(
              session.user.id,
              'initial_password_record'
            );
          }
        } catch (error) {
          console.error("Error recording password history:", error);
        }
      }
    };
    
    recordPasswordHistory();
  }, [session?.user?.id]);
  
  const value = {
    session,
    loading,
    signOut, 
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
